import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Gift, Lock } from "lucide-react";
import QuizEmailGate from "./QuizEmailGate";

const NAVY = "#102945";

/**
 * Soft-gate reveal page — shown to anonymous visitors the moment they
 * finish Q20. The persona identity (icon, name, tagline) is revealed
 * instantly with NO email required; the deep-dive breakdown, 4-week
 * plan, and contest entry unlock via the inline email form below.
 */
export default function QuizSoftReveal({ preview, leadToken, onResult }) {
  const {
    slug, name, icon, tagline, color,
    contest_guess: guess,
    contest_guess_name: guessName,
    contest_guess_correct: guessCorrect,
  } = preview || {};
  const shortName = (name || "").replace(/^The /, "");
  const registerHref = `/register?persona=${slug || ""}&ref=quiz-soft-reveal&utm_source=quiz&utm_medium=onsite_quiz&utm_campaign=soft-reveal&next=/app/scan`;

  const lockedTeasers = [
    {
      title: "Your MoneyMind Persona Chart",
      placeholder: "Planning, follow-through, savings consistency, risk response, decision confidence — the five",
    },
    {
      title: "How your persona can advance",
      placeholder: "Credit health, savings growth, financial security — your advantage, the obstacle, and the habit that",
    },
    {
      title: `Your three ${shortName} habits`,
      placeholder: "Written as If/Then loops you can switch on from day one, starting with the cue you already",
    },
    {
      title: `Your 4-week ${shortName} plan`,
      placeholder: "Week 1 starts with three concrete actions tuned to how you actually decide:",
    },
  ];

  return (
    <main className="flex-1 px-4 sm:px-6 py-10" data-testid="quiz-soft-reveal">
      <div className="max-w-2xl mx-auto">
        {/* Instant reveal — no email required */}
        <section
          className="p-8 sm:p-12 text-white rounded-xl shadow-md relative overflow-hidden"
          style={{ background: color || NAVY }}
          data-testid="soft-reveal-persona-card"
        >
          <p className="text-[11px] font-semibold uppercase tracking-widest text-white/70">
            Your MoneyMind&trade;
          </p>
          <div className="flex items-center gap-4 mt-3">
            <span className="text-5xl sm:text-6xl" aria-hidden>{icon}</span>
            <h1
              className="font-serif text-4xl sm:text-5xl leading-tight"
              data-testid="soft-reveal-persona-name"
            >
              {name}
            </h1>
          </div>
          {tagline && (
            <p className="mt-5 font-serif text-xl italic text-white/90">
              &ldquo;{tagline}&rdquo;
            </p>
          )}
        </section>

        {/* The offer, stated plainly — a completer who won't trade an email
            still has a door to the app (owner, 2026-10-18). */}
        <section
          className="mt-4 bg-white rounded-xl shadow-sm p-5 sm:p-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4"
          data-testid="soft-reveal-start-free"
        >
          <div>
            <p className="font-semibold text-[#2C302E]">
              Put the {shortName} pattern on a calendar.
            </p>
            <p className="mt-1 text-sm text-[#787671] inline-flex items-center gap-1.5">
              <Gift className="h-4 w-4 text-[#C05C3D]" /> No card needed · $10/month after, only if you stay
            </p>
          </div>
          <Link
            to={registerHref}
            className="shrink-0 inline-flex items-center justify-center gap-2 px-5 py-3 bg-[#2C302E] text-white rounded-sm hover:bg-[#1b1e1c] transition-colors"
            data-testid="soft-reveal-start-free-btn"
          >
            Start 60 days free <ArrowRight className="h-4 w-4" />
          </Link>
        </section>

        {guess && (
          <section
            className={`mt-4 rounded-xl border-l-4 p-5 shadow-sm ${guessCorrect ? "bg-[#F0F5EE]" : "bg-[#FBF6EC]"}`}
            style={{ borderColor: guessCorrect ? "#607762" : color || NAVY }}
            data-testid="soft-reveal-guess"
          >
            {guessCorrect ? (
              <p className="text-[#2C302E] leading-relaxed">
                🎯 <strong>You called it</strong> — you guessed {guessName} and
                that&apos;s exactly who you are. Enter your email below to lock in
                your entry for this month&apos;s <strong>free-year drawing</strong>{" "}
                ($120 value).
              </p>
            ) : (
              <p className="text-[#2C302E] leading-relaxed">
                You guessed <strong>{guessName}</strong> — but you&apos;re actually{" "}
                <strong>{name}</strong>. How we see ourselves and how we actually
                behave with money don&apos;t always match — that gap is exactly
                what your full breakdown explains.
              </p>
            )}
          </section>
        )}

        <QuizEmailGate personaPreview={preview} leadToken={leadToken} onResult={onResult} />

        {/* Locked depth teasers — real section titles, blurred body copy */}
        <section className="mt-6 space-y-3" data-testid="soft-reveal-locked-teasers">
          <p className="text-center text-xs font-semibold uppercase tracking-widest text-[#9b9892]">
            Waiting behind the unlock
          </p>
          {lockedTeasers.map((t) => (
            <div
              key={t.title}
              className="bg-white/70 border border-dashed border-[#C7C0B0] rounded-xl px-5 py-4"
            >
              <div className="flex items-baseline justify-between gap-4">
                <p className="text-sm font-semibold text-[#2C302E]">{t.title}</p>
                <span className="shrink-0 inline-flex items-center gap-1 text-[10px] uppercase tracking-wider text-[#A9712A] font-semibold">
                  <Lock className="h-3 w-3" /> Locked
                </span>
              </div>
              <p className="text-sm text-[#787671] mt-1 blur-sm select-none pointer-events-none" aria-hidden>
                {t.placeholder}
              </p>
            </div>
          ))}
        </section>

        <p className="mt-6 text-center text-[11px] text-[#9b9892]">
          For informational purposes only. Not financial advice.
        </p>
      </div>
    </main>
  );
}
